import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTasks } from '../../context/TaskContext';
import { useLoading } from '../../context/LoadingContext';
import { useRoutePath } from '../../utils/getRoutePath';
import Breadcrumb from '../common/Breadcrumb';
import {
  CalendarIcon,
  ArrowLeftIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserGroupIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

const TaskView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tasks, getTask, getTasks } = useTasks();
  const { hideLoading } = useLoading();
  const getRoutePath = useRoutePath();

  const [task, setTask] = useState(null);
  const [isFetching, setIsFetching] = useState(true);

  const hasFetchedTask = useRef(false);

  const fetchTask = useCallback(
    async () => {
      if (!id || hasFetchedTask.current) return;

      hasFetchedTask.current = true;
      setIsFetching(true);

      try {
        // Try to get from already loaded context first
        let found = getTask(id);

        // If not found (e.g. direct URL hit), load all tasks once
        if (!found) {
          const response = await getTasks(1, { all: true });

          if (response?.data) {
            const list = response.data;
            if (Array.isArray(list)) {
              found = list.find((item) => Number(item.id) === Number(id));
            }
          }
        }

        if (found) {
          setTask(found);
        } else {
          setTask(null);
        }
      } catch (error) {
        console.error('Error fetching task data:', error);
      } finally {
        setIsFetching(false);
        hideLoading();
      }
    },
    [id, getTask, getTasks, hideLoading]
  );

  useEffect(() => {
    // If list is already in context we might be able to resolve quickly
    if (id && tasks?.data?.length && !task) {
      const existing = tasks.data.find(
        (item) => Number(item.id) === Number(id)
      );
      if (existing) {
        setTask(existing);
        setIsFetching(false);
        return;
      }
    }

    fetchTask();
  }, [id, tasks, task, fetchTask]);

  const handleEdit = () => {
    navigate(getRoutePath(`/tasks/edit/${id}`));
  };

  const getStatusBadge = () => {
    const isActive = task?.active === 1 || task?.active;

    return (
      <span
        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
          isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}
      >
        {isActive ? (
          <>
            <CheckCircleIcon className="h-4 w-4 mr-1" />
            Active
          </>
        ) : (
          <>
            <XCircleIcon className="h-4 w-4 mr-1" />
            Inactive
          </>
        )}
      </span>
    );
  };

  const getCompletedBadge = () => {
    const isCompleted = task?.completed === 1 || task?.completed;

    return (
      <span
        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
          isCompleted ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}
      >
        {isCompleted ? 'Yes' : 'No'}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return timeString;
  };

  if (!isFetching && !task) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Task Not Found
          </h2>
          <p className="text-gray-600 mb-6">
            The task you&apos;re looking for doesn&apos;t exist or has been removed.
          </p>
          <button
            onClick={() => navigate(getRoutePath('/tasks'))}
            className="inline-flex items-center px-4 py-2 bg-[#009efb] text-white rounded-lg hover:bg-[#0089db] transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Tasks
          </button>
        </div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-3">
          <div className="h-8 w-8 border-2 border-[#009efb] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-600">Loading task information...</p>
        </div>
      </div>
    );
  }

  const assignedTo = Array.isArray(task.assign_to) ? task.assign_to : [];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-8">
          <Breadcrumb />
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mt-4 space-y-4 md:space-y-0">
            {/* Left side */}
            <div className="flex items-start md:items-center space-x-3">
              <div className="p-2 bg-[#009efb] rounded-lg">
                <CalendarIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center flex-wrap gap-2">
                  <span>{task?.task_type?.name || 'Task Details'}</span>
                </h1>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-600">
                  <span>{getStatusBadge()}</span>
                  <span>{getCompletedBadge()}</span>
                  {task.formated_created_at && (
                    <span className="text-gray-500">
                      Created: {task.formated_created_at}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Right side buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center sm:flex-wrap gap-2 w-full md:w-auto">
              <button
                onClick={() => navigate(getRoutePath('/tasks'))}
                className="inline-flex items-center justify-center px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-md bg-white hover:bg-gray-50 transition-colors w-full sm:w-auto"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back to List
              </button>

              <button
                onClick={handleEdit}
                className="inline-flex items-center justify-center px-4 py-2 text-sm bg-[#009efb] text-white rounded-md hover:bg-[#0089db] transition-colors w-full sm:w-auto"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                Edit Task
              </button>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="xl:col-span-2 space-y-6">
            {/* Task Info Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                <div className="flex items-center">
                  <CalendarIcon className="h-5 w-5 text-white mr-3" />
                  <h2 className="text-xl font-semibold text-white">
                    Task Information
                  </h2>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Task Type
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {task?.task_type?.name || 'N/A'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Privacy
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {task.privacy ? 'Everyone can see this task' : 'Private'}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date
                    </label>
                    <div className="flex items-center p-3 bg-gray-50 rounded-lg text-gray-900">
                      <CalendarIcon className="w-4 h-4 text-gray-400 mr-2" />
                      <span>{formatDate(task.start_date)}</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Date
                    </label>
                    <div className="flex items-center p-3 bg-gray-50 rounded-lg text-gray-900">
                      <CalendarIcon className="w-4 h-4 text-gray-400 mr-2" />
                      <span>{formatDate(task.end_date)}</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Time
                    </label>
                    <div className="flex items-center p-3 bg-gray-50 rounded-lg text-gray-900">
                      <ClockIcon className="w-4 h-4 text-gray-400 mr-2" />
                      <span>{formatTime(task.end_time)}</span>
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-900 min-h-[60px] whitespace-pre-wrap">
                    {task.description || 'No description provided.'}
                  </div>
                </div>
              </div>
            </div>

            {/* Recurring Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                <div className="flex items-center">
                  <ClockIcon className="h-5 w-5 text-white mr-3" />
                  <h2 className="text-xl font-semibold text-white">
                    Recurring Settings
                  </h2>
                </div>
              </div>
              <div className="p-6 space-y-4 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Recurring</span>
                  <span className="font-medium text-gray-900">
                    {task.recurring ? 'Yes' : 'No'}
                  </span>
                </div>

                {  task.recurring_note && (
                  <div className="pt-3 border-t border-gray-200">
                    <div className="text-xs font-medium text-gray-500 mb-1">
                      Summary
                    </div>
                    <div className="text-sm text-gray-900">
                      {task.recurring_note}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Assignees & Summary */}
          <div className="space-y-6">
            {/* Assignees Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900">
                  Assigned To
                </h2>
              </div>
              <div className="p-6 space-y-3 text-sm">
                {assignedTo.length > 0 ? (
                  assignedTo.map((user, index) => (
                    <div
                      key={index}
                      className="flex items-center space-x-3 p-2 rounded-md bg-gray-50"
                    >
                      <UserGroupIcon className="w-4 h-4 text-gray-400" />
                      <Link
                        to={getRoutePath(`/company-workforces/edit/${user.id}`)}
                        className="text-[#009efb] hover:text-[#0089db]"
                      >
                        {user.first_name} {user.last_name}
                      </Link>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500">No assignees.</p>
                )}
              </div>
            </div>

            {/* Task Summary Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900">
                  Task Summary
                </h2>
              </div>
              <div className="p-6 space-y-4 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Status</span>
                  {getStatusBadge()}
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Completed</span>
                  {getCompletedBadge()}
                </div>

                {task.formated_created_at && (
                  <div className="pt-3 border-t border-gray-200">
                    <div className="text-xs font-medium text-gray-500 mb-1">
                      Created At
                    </div>
                    <div className="text-sm text-gray-900">
                      {task.formated_created_at}
                    </div>
                  </div>
                )}

                {task.formated_updated_at && (
                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-1">
                      Last Updated
                    </div>
                    <div className="text-sm text-gray-900">
                      {task.formated_updated_at}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskView;

